/**
 * Inline dialer notice for lib/dialerCountryGate (desktop GlobalDialer + mobile
 * dialer): "Verify ID" for a country the account isn't entitled to, "Start
 * plan" for an entitled foreign country on a trial, and a plain support note
 * when even a verified account can't call there. A domestic trial destination
 * is left to TrialDestinationNotice (#1537), which already explains it.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, Sparkles } from 'lucide-react';
import { useDirectUpgrade } from '@/components/billing/DirectUpgradeSheet';
import { apiRequest } from '@/lib/queryClient';
import { startIdentityVerification, type CountryGate } from '@/lib/dialerCountryGate';

/** Account-level KYC state (GET /api/account/kyc); verified → KYC can't unlock more. */
export function useAccountKycVerified(): boolean {
  const { data } = useQuery<{ status?: string }>({ queryKey: ['/api/account/kyc'], staleTime: 5 * 60 * 1000, retry: false });
  return data?.status === 'verified';
}

export function CountryGateNotice({ gate, source }: { gate: CountryGate | null; source: string }) {
  const { available, openDirectUpgrade } = useDirectUpgrade();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!gate || gate.kind === 'allowed' || (gate.kind === 'trial' && gate.domestic)) return null;

  const verify = async () => {
    setBusy(true);
    setErr(null);
    const r = await startIdentityVerification((url, body) => apiRequest('POST', url, body));
    setBusy(false);
    if (r.url) window.location.href = r.url;
    else setErr(r.error || 'Could not start verification.');
  };
  const startPlan = () => {
    if (available) openDirectUpgrade({ feature: 'voice', source });
    else window.location.href = '/settings/billing';
  };
  const btn = 'inline-flex items-center gap-1.5 min-h-[36px] rounded-full px-3 font-semibold text-brand-ink bg-brand-lime hover:bg-brand-lime-hover disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <div
      className="rounded-xl border border-brand-lime bg-brand-lime-soft p-3 text-xs text-brand-ink dark:text-brand-lime-soft-foreground"
      role="status"
      data-testid={`country-gate-${gate.kind}`}
    >
      <p className="leading-snug">{gate.message}</p>
      {gate.kind === 'kyc' && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button type="button" onClick={verify} disabled={busy} className={btn} data-testid="button-country-gate-verify">
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            {busy ? 'Opening…' : 'Verify ID'}
          </button>
          {err && <span className="text-destructive">{err}</span>}
        </div>
      )}
      {gate.kind === 'trial' && (
        <div className="mt-2">
          <button type="button" onClick={startPlan} className={btn} data-testid="button-country-gate-start-plan">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Start plan
          </button>
        </div>
      )}
    </div>
  );
}
